<template>
    <div class="sticky top-0 bg-white z-10 pt-2 pb-2 -mx-4 px-4">
        <span>

            <Title>{{ currentPolicyData.title }}</Title>

            <NuxtLink :to="`/${publicSiteData.slug}/policies`" class="text-gray-700 font-light text-[15px] mr-2">
                Policy Manuals
            </NuxtLink> /
            <Dropdown v-if="policiesData.length > 1" :dropdown-icon="['fas', 'caret-down']" button-class="border-none !bg-white">
                <template v-slot:trigger><span class="text-[15px]">{{currentPolicyData.title}}</span></template>
                <DropdownItem
                    v-for="policyManual in policiesData"
                    v-show="policyManual.slug !== currentPolicyData.slug"
                    :to="`/${publicSiteData.slug}/policies/${policyManual.slug}`"
                >
                    {{policyManual.title}}
                </DropdownItem>
            </Dropdown>
            <span v-else class="text-[15px]">{{currentPolicyData.title}}</span>
        </span>
        <Heading1 class="mt-2 mb-0">{{currentPolicyData.title}}</Heading1>
    </div>
    <div v-if="hasDescription"
        :class="proseClasses + ' text-gray-700 max-w-full'"
        v-html="currentPolicyData.description"
    ></div>

    <div class="w-full flex flex-1 mt-6">
        <div class="min-w-54 max-w-80 pe-6 hidden sm:block">
            <div class="sticky top-24 max-h-[calc(100vh-6rem)] flex flex-col">
                <TextInput placeholder="Search items" :icon="['fas', 'magnifying-glass']" v-model="search" class="mb-2 w-full shrink-0" />
                <div class="flex flex-col text-sm gap-y-4 overflow-y-auto">
                    <div v-for="section in sections" :key="section.id" class="py-1 flex flex-col gap-y-1">
                        <a
                            :href="`#${sectionAnchor(section)}`"
                            v-show="!lowerSearch || section.title?.toLowerCase().includes(lowerSearch) || section.code?.toLowerCase().includes(lowerSearch)"
                            class="flex items-center rounded-lg px-1 text-black"
                        >
                            <span class="w-2/5 text-gray-700 text-left break-all">{{section.code}}</span>
                            <span class="w-3/5 font-medium text-left break-words hyphens-auto pl-2">{{section.title}}</span>
                        </a>
                        <div class="flex flex-col">
                            <a
                                v-for="item in section.items"
                                :href="`#${itemAnchor(section, item)}`"
                                v-show="!lowerSearch || item.title?.toLowerCase().includes(lowerSearch) || item.code?.toLowerCase().includes(lowerSearch)"
                                class="flex items-stretch rounded-lg text-black"
                            >
                                <div class="w-2/5 flex gap-x-2 items-stretch"><div class="border-l border-gray-400 pl-4 ml-4 flex items-center"><span class="text-gray-700 break-words">{{item.code}}</span></div></div>
                                <span class="w-3/5 text-start py-1 break-words hyphens-auto pl-2">{{item.title}}</span>
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="flex-1 sm:border-l border-gray-400 !h-full sm:pl-10 max-w-full">
            <div v-if="sections.length === 0" class="px-8 py-12 flex flex-col items-center gap-y-4 text-gray-700">
                <h2 class="text-xl font-medium">This manual is empty</h2>
            </div>
            <article v-else>
                <div v-for="section in sections" class="mb-12">
                    <h2 :id="sectionAnchor(section)" class="text-2xl mb-6 break-words hyphens-auto scroll-mt-24">{{section.code}}: {{section.title}}</h2>
                    <div v-for="item in section.items">
                        <h3 :id="itemAnchor(section, item)" class="text-lg mb-6 scroll-mt-24">{{item.code}} {{item.title}}</h3>
                        <div v-html="item.content" :class="proseClasses"></div>
                        <ClientOnly>
                            <div v-if="item.attachments && item.attachments.length" class="flex flex-col gap-y-4">
                                <PDFViewer v-for="attachment in item.attachments" :key="attachment.uuid" :src="attachment.url" :pdf-title="attachment.title || attachment.name || attachment.filename || 'Document'" :site-slug="route.params.site_slug" :page-slug="route.params.slug" page-type="policy" />
                            </div>
                            <template #fallback>
                                <div v-if="item.attachments && item.attachments.length" class="flex flex-col gap-y-4">
                                    <div v-for="attachment in item.attachments" :key="attachment.uuid" class="w-full" aria-hidden="true">
                                        <div class="h-9 mb-4"></div>
                                        <div class="w-full min-h-[80vh]"></div>
                                    </div>
                                </div>
                            </template>
                        </ClientOnly>
                        <div v-if="!!item.adopted_at || !!item.last_revised_at || !!item.last_reviewed_at"
                             class="flex items-center gap-x-4 text-gray-700 text-sm mt-4 mb-6"
                             :class="proseClasses"
                        >
                            <span v-if="!!item.adopted_at">Adopted: {{item.adopted_at}}</span>
                            <span v-if="!!item.last_revised_at">Last revised: {{item.last_revised_at}}</span>
                            <span v-if="!!item.last_reviewed_at">Last reviewed: {{item.last_reviewed_at}}</span>
                        </div>
                    </div>
                </div>
            </article>
        </div>
    </div>
</template>

<script setup>
import Dropdown from "~/components/ui/Dropdown.vue";
import DropdownItem from "~/components/ui/DropdownItem.vue";
import Heading1 from "~/components/public/Heading1.vue";
import {computed, nextTick, onMounted} from "vue";
import TextInput from "~/components/ui/TextInput.vue";
import PDFViewer from "~/components/pdf/PDFViewer.vue";

const route = useRoute();
const publicSiteData = useState('publicSiteData');
const { proseClasses } = useProseClasses();

const search = ref('');

const { data: policiesData } = await publicFetch(`/public-sites/policy-books`);
const { data: currentPolicyData } = await publicFetch(`/public-sites/policy-books/${route.params.slug}`);

// Codes can contain dots and other characters that are invalid in CSS selectors
// (e.g. "2.2" would make "#section-2-item-2.2" parse as an id plus a class),
// so strip them out before using a code in an anchor id.
const slugifyCode = (code) => String(code ?? '').replace(/[^a-zA-Z0-9_-]+/g, '-');
const sectionAnchor = (section) => `section-${slugifyCode(section.code)}`;
const itemAnchor = (section, item) => `${sectionAnchor(section)}-item-${slugifyCode(item.code)}`;

const sections = computed(() => currentPolicyData.sections);
const lowerSearch = computed(() => search.value.toLowerCase());
const hasDescription = computed(() => {
    const { hasHtmlContent } = useHtmlUtils();
    return hasHtmlContent(currentPolicyData?.description);
});

// The router resolves the hash once, before the sections have been fetched and
// rendered on the client, so it can miss the heading entirely. Scroll to it
// ourselves once the content is on the page.
onMounted(async () => {
    if (!route.hash) {
        return;
    }
    await nextTick();
    document.getElementById(route.hash.slice(1))?.scrollIntoView();
});
</script>
